// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseCheckbox, BaseInput, ConfirmModal, Loading } from '~/components/common';
// Others
import { jobActions } from '~/thunks/job/jobSlice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { checklistActions } from '~/thunks/checklist/checklistSlice';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { IChecklist, IFormCheckListItem } from '~/utils/interface/checklist';
import { createChecklistItem, deleteChecklist, deleteChecklistItem } from '~/thunks/checklist/checklistThunk';
// Styles, images, icons
import styles from './ColumnCheckList.module.scss';
import { icons } from '~/assets';

type Props = {
  checkListData?: IChecklist;
  isDelete?: boolean;
  isDeleteItem?: boolean;
};

const cx = classNames.bind(styles);

const ColumnCheckList = (props: Props) => {
  //#region Destructuring Props
  const { checkListData, isDelete, isDeleteItem } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addValue, setAddValue] = useState<string>(EMPTY_STRING);

  const [isDeleteChecklist, setIsDeleteChecklist] = useState<boolean>(false);
  const [isDeleteChecklistItem, setIsDeleteChecklistItem] = useState<boolean>(false);

  const [idChecklist, setIdChecklist] = useState<string>(EMPTY_STRING);
  const [idChecklistItem, setIdChecklistItem] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleAdd = () => {
    if (!checkListData || !addValue) return;

    const data: IFormCheckListItem = {
      checklistId: checkListData?.id || EMPTY_VALUE,
      name: addValue,
    };

    setIsLoading(true);

    dispatch(createChecklistItem(data))
      .unwrap()
      .then((res) => {
        setAddValue(EMPTY_STRING);
        dispatch(jobActions.setRefreshJobList(true));
        dispatch(checklistActions.setRefreshChecklistList(true));
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleConfirmDeleteChecklist = () => {
    setIsLoading(true);

    dispatch(deleteChecklist(idChecklist))
      .unwrap()
      .then(() => {
        setIdChecklist(EMPTY_STRING);
        handleCloseDeleteChecklist();
        dispatch(jobActions.setRefreshJobList(true));
        dispatch(checklistActions.setRefreshChecklistList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleConfirmDeleteChecklistItem = () => {
    setIsLoading(true);

    dispatch(deleteChecklistItem(idChecklistItem))
      .unwrap()
      .then(() => {
        setIdChecklistItem(EMPTY_STRING);
        handleCloseDeleteChecklistItem();
        dispatch(jobActions.setRefreshJobList(true));
        dispatch(checklistActions.setRefreshChecklistList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseDeleteChecklist = () => {
    setIsDeleteChecklist(false);
  };

  const handleCloseDeleteChecklistItem = () => {
    setIsDeleteChecklistItem(false);
  };
  //#endregion Handle Function

  return (
    <Box className={cx('container')}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={4}>
        <Typography className={cx('title')}>{checkListData?.title || EMPTY_STRING}</Typography>

        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box className={cx('item')}>
            {t('job_detail_title_items', {
              value: checkListData?.checklistItems?.length,
            })}
          </Box>

          {isDelete && (
            <IconButton
              className={cx('buttonRemove')}
              onClick={() => {
                setIdChecklist(checkListData?.id || EMPTY_STRING);
                setIsDeleteChecklist(true);
              }}
            >
              <img
                className={cx('icon')}
                src={icons.commonIconTrash}
                alt={t('common_alt_img', { ns: 'common/base' })}
              />
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={4}>
        <Box className={cx('input')}>
          <BaseInput
            height={42}
            placeholder={t('job_detail_title_add_new_checklist_item')}
            value={addValue}
            onChange={(data) => setAddValue(data?.target?.value)}
          />
        </Box>

        <Button type='button' variant='contained' className={cx('buttonAction')} onClick={handleAdd}>
          {t('job_detail_title_add')}
        </Button>
      </Stack>

      <Box className={cx('listChildren')}>
        {checkListData?.checklistItems && checkListData?.checklistItems?.length > 0 ? (
          checkListData?.checklistItems?.map((item, index) => {
            return (
              <Stack className={cx('itemChildren')} direction={'row'} alignItems={'center'} gap={2} key={index}>
                <BaseCheckbox checked={item.isChecked} />

                <Typography className={cx('textChildren')}>{item?.name}</Typography>

                {isDeleteItem && (
                  <IconButton
                    className={cx('buttonRemove')}
                    onClick={() => {
                      setIdChecklistItem(item?.id);
                      setIsDeleteChecklistItem(true);
                    }}
                  >
                    <img
                      className={cx('icon')}
                      src={icons.commonIconTrash}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                    />
                  </IconButton>
                )}
              </Stack>
            );
          })
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        )}
      </Box>

      {isDeleteChecklist && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseDeleteChecklist}
          onConfirm={handleConfirmDeleteChecklist}
        />
      )}

      {isDeleteChecklistItem && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseDeleteChecklistItem}
          onConfirm={handleConfirmDeleteChecklistItem}
        />
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default ColumnCheckList;
